// ---------------------------------------------------------------------------
// DatasetSearchField — the "Dataset" search Combobox backed by the Data Lake
// search results the parent supplies. It owns only presentation: rendering the
// options, the loading/empty states, and the missing/resolve-error warnings.
// Selection and query state live in the parent so the version + file-format
// fields can react to them.
// ---------------------------------------------------------------------------

import { Combobox, Field, Option } from '@fluentui/react-components';

import type { Dataset } from '../../../../../services/dataHubService.interface.js';

import { renderOptionText } from './datasetOption.js';

export function DatasetSearchField({
  datasets,
  isLoading,
  query,
  onQueryChange,
  selectedId,
  disabled,
  resolveError,
  showMissingWarning,
  onSelect,
  onValidatedIdChange,
}: {
  datasets: Dataset[];
  isLoading: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  selectedId: string | null;
  disabled: boolean | undefined;
  resolveError: string | null;
  showMissingWarning: boolean;
  onSelect: (
    selection: {
      id: string;
      version: number | null;
      displayName: string;
    } | null,
  ) => void;
  onValidatedIdChange: (id: string | null) => void;
}) {
  return (
    <Field
      label="Dataset"
      required
      validationState={
        resolveError !== null
          ? 'error'
          : showMissingWarning
            ? 'warning'
            : 'none'
      }
      validationMessage={
        resolveError !== null
          ? `Couldn't load the saved dataset (${selectedId ?? ''}): ${resolveError}`
          : showMissingWarning
            ? `Selected dataset (${selectedId ?? ''}) is not in the current search results — refine your search to confirm it still exists.`
            : undefined
      }
    >
      <Combobox
        aria-label="Search datasets"
        placeholder={
          isLoading
            ? 'Loading datasets…'
            : selectedId !== null && query === ''
              ? // Surface the id as a fallback when we have a saved
                // selection but haven't resolved its name yet — so the
                // user knows the form remembers the selection.
                selectedId
              : 'Search datasets…'
        }
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        selectedOptions={selectedId !== null ? [selectedId] : []}
        onOptionSelect={(_e, data) => {
          const id = data.optionValue;
          if (id === undefined) {
            onSelect(null);
            onValidatedIdChange(null);
            return;
          }
          const ds = datasets.find((d) => d.id === id);
          if (ds) {
            onSelect({
              id: ds.id,
              version: null,
              displayName: renderOptionText(ds),
            });
            onQueryChange(renderOptionText(ds));
            // Mark this id as user-validated so the "missing"
            // warning doesn't fire when the post-selection search
            // returns a different set of results.
            onValidatedIdChange(ds.id);
          }
        }}
        disabled={disabled}
      >
        {isLoading && datasets.length === 0 && (
          <Option value="" disabled>
            Loading…
          </Option>
        )}
        {!isLoading && datasets.length === 0 && (
          <Option value="" disabled>
            No datasets match
          </Option>
        )}
        {datasets.map((d) => {
          // The Datalake API sometimes returns `description: null` even
          // though our TS type marks it optional; treat both null and
          // empty-string as "no description".
          const description = d.description ?? '';
          return (
            <Option key={d.id} value={d.id} text={renderOptionText(d)}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600 }}>
                  {d.name} · v{String(d.version)}
                </span>
                <span
                  style={{
                    fontSize: '0.75rem',
                    opacity: 0.6,
                    fontFamily: 'monospace',
                  }}
                >
                  {d.id}
                </span>
                {description.length > 0 && (
                  <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                    {description}
                  </span>
                )}
              </div>
            </Option>
          );
        })}
      </Combobox>
    </Field>
  );
}
